import { useState } from '@wordpress/element';

export default function Membership() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('AT');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [vin, setVin] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldErrorMessages, setFieldErrorMessages] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const clearFieldError = (fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
    setFieldErrorMessages(prev => {
      const newMessages = { ...prev };
      delete newMessages[fieldName];
      return newMessages;
    });
    setValidationErrors([]);
  };

  const validateForm = () => {
    const errors = [];
    const fields = {};
    const messages = {};

    if (!firstName.trim()) {
      errors.push('Bitte gib deinen Vornamen ein.');
      fields.firstName = true;
      messages.firstName = 'Bitte gib deinen Vornamen ein.';
    }
    if (!lastName.trim()) {
      errors.push('Bitte gib deinen Nachnamen ein.');
      fields.lastName = true;
      messages.lastName = 'Bitte gib deinen Nachnamen ein.';
    }
    if (!street.trim()) {
      errors.push('Bitte gib deine Straße ein.');
      fields.street = true;
      messages.street = 'Bitte gib deine Straße ein.';
    }
    if (!houseNumber.trim()) {
      errors.push('Bitte gib deine Hausnummer ein.');
      fields.houseNumber = true;
      messages.houseNumber = 'Bitte gib deine Hausnummer ein.';
    }
    if (!postalCode.trim()) {
      errors.push('Bitte gib deine Postleitzahl ein.');
      fields.postalCode = true;
      messages.postalCode = 'Bitte gib deine Postleitzahl ein.';
    }
    if (!city.trim()) {
      errors.push('Bitte gib deinen Ort ein.');
      fields.city = true;
      messages.city = 'Bitte gib deinen Ort ein.';
    }
    if (!email.trim()) {
      errors.push('Bitte gib deine E-Mail-Adresse ein.');
      fields.email = true;
      messages.email = 'Bitte gib deine E-Mail-Adresse ein.';
    } else if (!validateEmail(email)) {
      errors.push('Bitte gib eine gültige E-Mail-Adresse ein.');
      fields.email = true;
      messages.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    }
    if (!vin.trim()) {
      errors.push('Bitte gib die Fahrgestellnummer (FIN) ein.');
      fields.vin = true;
      messages.vin = 'Bitte gib die Fahrgestellnummer (FIN) ein.';
    } else if (vin.trim().length !== 17) {
      errors.push('Die Fahrgestellnummer (VIN) muss genau 17 Zeichen lang sein.');
      fields.vin = true;
      messages.vin = 'Die VIN muss genau 17 Zeichen lang sein.';
    }
    if (!privacyConsent) {
      errors.push('Bitte stimme der Datenschutzerklärung zu.');
      fields.privacyConsent = true;
      messages.privacyConsent = 'Bitte stimme der Datenschutzerklärung zu.';
    }

    setValidationErrors(errors);
    setFieldErrors(fields);
    setFieldErrorMessages(messages);
    return errors.length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      const firstError = document.querySelector('.field-error, .field-error-section');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    const formData = {
      firstName,
      lastName,
      street,
      houseNumber,
      postalCode,
      city,
      country,
      phone,
      email,
      birthdate,
      vin: vin.trim(),
      privacyConsent,
    };

    fetch('/wp-json/cqvs/v1/membership', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
      .then(response => response.json())
      .then(data => {
        setIsSubmitting(false);
        if (data.success) {
          setIsSubmitted(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const msg = data.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.';
          setValidationErrors([msg]);
        }
      })
      .catch(error => {
        setIsSubmitting(false);
        console.error('Error:', error);
        setValidationErrors(['Es ist ein Fehler aufgetreten. Bitte versuche es erneut.']);
      });
  };

  if (isSubmitted) {
    return (
      <div className="membership-success">
        <h2>Vielen Dank für deine Registrierung!</h2>
        <p>Deine Daten wurden erfolgreich übermittelt. Wir melden uns bei dir, sobald alles bearbeitet ist.</p>
      </div>
    );
  }

  return (
    <>
      <div className="fields-row">
        <div className="field-wrap">
          <label htmlFor="membership-firstName">Vorname *</label>
          <input
            type="text"
            id="membership-firstName"
            className={fieldErrors.firstName ? 'field-error' : ''}
            value={firstName}
            required
            autoComplete="given-name"
            onChange={(e) => { setFirstName(e.target.value); clearFieldError('firstName'); }}
          />
          {fieldErrorMessages.firstName && <div className="field-error-message">{fieldErrorMessages.firstName}</div>}
        </div>
        <div className="field-wrap">
          <label htmlFor="membership-lastName">Nachname *</label>
          <input
            type="text"
            id="membership-lastName"
            className={fieldErrors.lastName ? 'field-error' : ''}
            value={lastName}
            required
            autoComplete="family-name"
            onChange={(e) => { setLastName(e.target.value); clearFieldError('lastName'); }}
          />
          {fieldErrorMessages.lastName && <div className="field-error-message">{fieldErrorMessages.lastName}</div>}
        </div>
      </div>
      <div className="fields-row">
        <div className="field-wrap field-wrap-street">
          <label htmlFor="membership-street">Straße *</label>
          <input
            type="text"
            id="membership-street"
            className={fieldErrors.street ? 'field-error' : ''}
            value={street}
            required
            autoComplete="address-line1"
            onChange={(e) => { setStreet(e.target.value); clearFieldError('street'); }}
          />
          {fieldErrorMessages.street && <div className="field-error-message">{fieldErrorMessages.street}</div>}
        </div>
        <div className="field-wrap field-wrap-house-number">
          <label htmlFor="membership-houseNumber">Hausnr. *</label>
          <input
            type="text"
            id="membership-houseNumber"
            className={fieldErrors.houseNumber ? 'field-error' : ''}
            value={houseNumber}
            required
            onChange={(e) => { setHouseNumber(e.target.value); clearFieldError('houseNumber'); }}
          />
          {fieldErrorMessages.houseNumber && <div className="field-error-message">{fieldErrorMessages.houseNumber}</div>}
        </div>
      </div>

      <div className="fields-row">
        <div className="field-wrap field-wrap-zip">
          <label htmlFor="membership-postalCode">PLZ *</label>
          <input
            type="text"
            id="membership-postalCode"
            className={fieldErrors.postalCode ? 'field-error' : ''}
            value={postalCode}
            required
            autoComplete="postal-code"
            onChange={(e) => { setPostalCode(e.target.value); clearFieldError('postalCode'); }}
          />
          {fieldErrorMessages.postalCode && <div className="field-error-message">{fieldErrorMessages.postalCode}</div>}
        </div>
        <div className="field-wrap field-wrap-city">
          <label htmlFor="membership-city">Ort *</label>
          <input
            type="text"
            id="membership-city"
            className={fieldErrors.city ? 'field-error' : ''}
            value={city}
            required
            autoComplete="address-level2"
            onChange={(e) => { setCity(e.target.value); clearFieldError('city'); }}
          />
          {fieldErrorMessages.city && <div className="field-error-message">{fieldErrorMessages.city}</div>}
        </div>
      </div>

      <div className="fields-row">
        <div className="field-wrap field-wrap-country">
          <label htmlFor="membership-country">Land *</label>
          <select
            id="membership-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="AT">Österreich</option>
            <option value="DE">Deutschland</option>
            <option value="CH">Schweiz</option>
          </select>
        </div>
        <div className="field-wrap field-wrap-birthdate">
          <label htmlFor="membership-birthdate">Geburtsdatum</label>
          <input
            type="date"
            id="membership-birthdate"
            value={birthdate}
            max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
            onChange={(e) => setBirthdate(e.target.value)}
          />
        </div>
      </div>
      <div className="fields-row">
        <div className="field-wrap field-wrap-email">
          <label htmlFor="membership-email">E-Mail *</label>
          <input
            type="email"
            id="membership-email"
            className={fieldErrors.email ? 'field-error' : ''}
            value={email}
            required
            autoComplete="email"
            onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
          />
          {fieldErrorMessages.email && <div className="field-error-message">{fieldErrorMessages.email}</div>}
        </div>
        <div className="field-wrap field-wrap-phone">
          <label htmlFor="membership-phone">Telefon</label>
          <input
            type="tel"
            id="membership-phone"
            className={fieldErrors.phone ? 'field-error' : ''}
            value={phone}
            autoComplete="tel"
            onChange={(e) => { setPhone(e.target.value); clearFieldError('phone'); }}
          />
          {fieldErrorMessages.phone && <div className="field-error-message">{fieldErrorMessages.phone}</div>}
        </div>
      </div>



      <div className="fields-row">
        <div className="field-wrap field-wrap-vin">
          <label htmlFor="membership-vin">Fahrgestellnummer (FIN) *</label>
          <input
            type="text"
            id="membership-vin"
            className={fieldErrors.vin ? 'field-error' : ''}
            value={vin}
            required
            maxLength={17}
            placeholder="z.B. WVWZZZ1JZXW000001"
            onChange={(e) => { setVin(e.target.value.toUpperCase()); clearFieldError('vin'); }}
          />
          {fieldErrorMessages.vin && <div className="field-error-message">{fieldErrorMessages.vin}</div>}
          <p className="field-hint">Die FIN findest du im Fahrzeugbrief. Sie besteht aus 17 Zeichen.</p>
        </div>
      </div>

      <div className="privacy-consent">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={privacyConsent}
            className={fieldErrors.privacyConsent ? 'field-error' : ''}
            onChange={(e) => { setPrivacyConsent(e.target.checked); clearFieldError('privacyConsent'); }}
          />
          <span>Ich habe die <a href="/datenschutz" target="_blank">Datenschutzerklärung</a> zur Kenntnis genommen. Ich stimme zu, dass meine Angaben und Daten zur Beantwortung meiner Anfrage elektronisch erhoben und gespeichert werden. *</span>
        </label>
        {fieldErrorMessages.privacyConsent && <div className="field-error-message">{fieldErrorMessages.privacyConsent}</div>}
      </div>

      <button
        className="submit-button button"
        disabled={isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? 'Wird gesendet...' : 'Registrierung absenden'}
      </button>

      {validationErrors.length > 0 && (
        <div className="validation-errors">
          <h4>Bitte überprüfe deine Eingaben:</h4>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
